import { z } from "zod";

let cachedIsPossiblePhoneNumber: (text: string) => boolean;

const getIsPossiblePhoneNumber = async () => {
  if (!cachedIsPossiblePhoneNumber)
    cachedIsPossiblePhoneNumber = (await import("libphonenumber-js"))
      .isPossiblePhoneNumber;
  return cachedIsPossiblePhoneNumber;
};

export const emailAuthFlowSchema = z.object({
  email: z.email("web_form_error_invalid_email"),
});
export type EmailAuthFlowForm = z.infer<typeof emailAuthFlowSchema>;

export const preexistingPasswordAuthFlowSchema = z.object({
  password: z.string().min(1, "input_validation_error_required"),
});

const passwordSchema = z
  .string()
  .min(8, "password_input_hint_length")
  .superRefine((val, ctx) => {
    if (/(.)\1\1\1/g.test(val)) {
      ctx.addIssue({
        code: "custom",
        message: "web_error_password_repetition",
      });
    }
    if (
      /abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz|0123|1234|2345|3456|4567|5678|6789|7890/i.test(
        val,
      )
    ) {
      ctx.addIssue({
        code: "custom",
        message: "web_error_password_sequence",
      });
    }
    if (DISALLOWED_PASSWORDS.includes(val)) {
      ctx.addIssue({
        code: "custom",
        message: "web_error_disallowed_password",
      });
    }
  });

export const passwordAuthFlowSchema = z.object({
  password: passwordSchema,
});
export type PasswordAuthFlowForm = z.infer<typeof passwordAuthFlowSchema>;

export const changePasswordSchema = z.object({
  current_password: z.string().min(1, "input_validation_error_required"),
  new_password: passwordSchema,
});
export type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

export const phoneAuthFlowSchema = z.object({
  phone: z
    .string({ error: "web_payments_phone_number_incomplete" })
    .superRefine(async (val, ctx) => {
      const isPossiblePhoneNumber = await getIsPossiblePhoneNumber();
      if (!isPossiblePhoneNumber(val ?? "")) {
        ctx.addIssue({
          code: "custom",
          message: "error_invalid_phone_number",
        });
      }
    }),
});

const DISALLOWED_PASSWORDS = [
  "hallowapp",
  "picture1",
  "password",
  "Million2",
  "iloveyou",
  "aaron431",
  "password1",
  "qqww1122",
  "qwertyuiop",
  "asdfghjkl",
  "987654321",
  "chatbooks",
  "123123123",
  "princess",
  "jacket025",
  "sunshine",
  "1q2w3e4r",
  "qwerty123",
  "Bangbang123",
  "jobandtalent",
  "football",
  "baseball",
  "superman",
  "samantha",
  "babygirl",
  "jordan23",
  "basketball",
  "michelle",
  "chocolate",
  "computer",
  "password123",
  "iloveyou1",
  "0987654321",
  "blink182",
  "princess1",
  "fuckyou1",
  "trustno1",
  "butterfly",
  "x4ivygA51F",
  "starwars",
  "michael1",
  "jakcgt333",
  "jennifer",
  "babygirl1",
  "sample123",
  "football1",
  "whatever",
  "alexander",
  "passw0rd",
  "11223344",
];

export const customEmailAuthSchema = (re: RegExp, error: string) =>
  z.object({ email: z.string().regex(re, error) });

export type PhoneAuthFlowForm = z.infer<typeof phoneAuthFlowSchema>;
